import type { BaseQueryFn } from '@reduxjs/toolkit/query/react'
import { Mutex } from 'async-mutex'
import type { AxiosError, AxiosRequestConfig } from 'axios'

import { api } from 'src/common/api'

import { resetAuthStore, setAuthInfo } from '@v2/domains/auth/store/auth.store'
import { isCookieAuthenticationEnabled } from '@v2/domains/auth/utils/isCookieAuthenticationEnabled'

interface BaseQueryArgs {
  url: string
  method: AxiosRequestConfig['method']
  data?: AxiosRequestConfig['data']
  params?: AxiosRequestConfig['params']
  headers?: AxiosRequestConfig['headers']
  paramsSerializer?: AxiosRequestConfig['paramsSerializer']
  responseType?: AxiosRequestConfig['responseType']
}

// Mutex to prevent multiple concurrent token refresh requests
const mutex = new Mutex()

/**
 * Base query function with automatic token refresh on 401
 *
 * Flow:
 * 1. Execute original request
 * 2. If 401 → Acquire mutex
 * 3. Attempt token refresh
 * 4. If refresh succeeds → Retry original request
 * 5. If refresh fails → Logout user
 */
const axiosBaseQuery =
  (
    { baseUrl } = { baseUrl: '' },
  ): BaseQueryFn<BaseQueryArgs, unknown, unknown> =>
  async (args, api) => {
    // Wait for any ongoing token refresh to complete
    await mutex.waitForUnlock()

    // Execute the original request
    let result = await baseRequest(args, baseUrl, api.signal)

    // Handle 401 Unauthorized
    if (
      result.error &&
      (result.error as unknown as { status: number }).status === 401
    ) {
      // Cookie refresh and retry are handled by the shared Axios interceptor.
      // Re-entering the legacy token flow here would reset the cookie session
      // because cookie authentication intentionally has no Redux refresh token.
      if (isCookieAuthenticationEnabled) {
        return result
      }

      console.log(
        `%c[BaseQuery] ⚠️  401 UNAUTHORIZED`,
        'background: #f59e0b; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
        `\n  🌐 Endpoint: ${args.url}\n  🔍 Verificando si se debe hacer refresh...`,
      )

      // Skip refresh for certain endpoints (login, refresh itself)
      const skipRefreshUrls = [
        '/v3/auth/login',
        '/v3/auth/refresh',
        '/v3/auth/forgot-password',
      ]
      const shouldSkipRefresh = skipRefreshUrls.some((url) =>
        args.url.includes(url),
      )

      if (shouldSkipRefresh) {
        return result
      }

      // Check if another request is already refreshing
      if (!mutex.isLocked()) {
        const release = await mutex.acquire()

        try {
          const state = api.getState() as unknown as {
            auth: {
              refreshToken?: string
              organizationId?: string
              membershipId?: string
            }
          }
          const refreshToken = state.auth.refreshToken
          const organizationId = state.auth.organizationId
          const membershipId = state.auth.membershipId

          if (!refreshToken) {
            api.dispatch(resetAuthStore())

            // Redirect to login after state is cleared
            setTimeout(() => {
              window.location.href = '/login'
            }, 100)

            return result
          }

          // Attempt to refresh the token with organization context
          const refreshResult = await baseRequest(
            {
              url: `${baseUrl}/v3/auth/refresh`,
              method: 'POST',
              data: {
                refreshToken,
                ...(organizationId && { organizationId }),
                ...(membershipId && { membershipId }),
              },
            },
            baseUrl,
            api.signal,
          )

          if (refreshResult.data) {
            const tokens = refreshResult.data as {
              access_token: string
              refresh_token: string
              userId: string
            }

            // Update Redux store with new tokens and organization context
            api.dispatch(
              setAuthInfo({
                access_token: tokens.access_token,
                refresh_token: tokens.refresh_token,
                userId: tokens.userId,
                ...(organizationId && { organizationId }),
                ...(membershipId && { membershipId }),
              }),
            )

            // Retry the original request with new token
            result = await baseRequest(args, baseUrl, api.signal)
          } else {
            api.dispatch(resetAuthStore())

            setTimeout(() => {
              window.location.href = '/login'
            }, 100)
          }
        } catch (refreshError) {
          // Refresh request threw an error → Force logout
          api.dispatch(resetAuthStore())

          setTimeout(() => {
            window.location.href = '/login'
          }, 100)
        } finally {
          release()
        }
      } else {
        // Another request is refreshing, wait for it to complete
        console.log(
          `%c[BaseQuery] ⏳ ESPERANDO MUTEX`,
          'background: #f59e0b; color: white; padding: 4px 8px; border-radius: 4px;',
          '\n  ⏳ Otro request está haciendo refresh\n  ⌛ Esperando a que termine...',
        )
        await mutex.waitForUnlock()

        console.log(
          `%c[BaseQuery] 🔓 MUTEX DESBLOQUEADO`,
          'background: #10b981; color: white; padding: 4px 8px; border-radius: 4px;',
          `\n  🔄 Reintentando request: ${args.url}`,
        )

        // Retry the original request after refresh completes
        result = await baseRequest(args, baseUrl, api.signal)
      }
    }

    return result
  }

/**
 * Base request executor
 */
async function baseRequest(
  args: BaseQueryArgs,
  baseUrl: string,
  signal: AbortSignal,
) {
  try {
    const result = await api({
      url: `${baseUrl}${args.url}`,
      method: args.method,
      data: args.data,
      params: args.params,
      headers: args.headers,
      paramsSerializer: args.paramsSerializer,
      responseType: args.responseType,
      signal,
    })
    return { data: result.data }
  } catch (axiosError) {
    const err = axiosError as AxiosError
    return {
      error: {
        status: err.response?.status,
        data: err.response?.data || err.message,
      },
    }
  }
}

export default axiosBaseQuery
